export interface IDesignSize {
  width: number;
  height: number;
}

const FALLBACK_DESIGN_SIZE: IDesignSize = { width: 1920, height: 1080 };

/**
 * Maps an authored design aspect ratio to the canonical design-space size in
 * px. The absolute numbers only need to be internally consistent across a
 * composition (renderers scale design coords to the live rect); they are kept
 * in step with the OverlayBuilder's canvas defaults.
 */
export const designSizeForRatio = (ratio: string | undefined): IDesignSize => {
  switch (ratio) {
    case "4:3":
      return { width: 1920, height: 1440 };
    case "1:1":
      return { width: 1920, height: 1920 };
    case "9:16":
      return { width: 1080, height: 1920 };
    case "21:9":
      return { width: 2520, height: 1080 };
    // Banner / panel (misma tabla que usaba CustomOverlayRenderer).
    case "10:3":
      return { width: 1200, height: 360 };
    case "16:9":
    case "free":
    default:
      return FALLBACK_DESIGN_SIZE;
  }
};

/**
 * Resolves the design-space an alert composition was authored against: an
 * explicit `designSize` (px) wins; otherwise it derives from the ratio table.
 * Every lane that scales an alert composition into its slot (DOM AlertSlot,
 * test-fire layer, Pixi AlertsFactory) resolves through this so they agree.
 */
export const resolveAlertDesignSize = (composition: {
  designSize?: IDesignSize;
  designAspectRatio?: string;
}): IDesignSize =>
  composition.designSize ?? designSizeForRatio(composition.designAspectRatio);

/**
 * Design space de un overlay CUSTOM (área, scene mask y el render Pixi): el
 * `designSize` explícito gana; si no, la tabla por `designAspectRatio`; con
 * "free" (o sin ratio) el `fallback` del llamador (rect del área / lienzo).
 * Los tres carriles resuelven por acá para que un `translate`/`origin` de
 * keyframes en px de diseño escale igual en el editor (DOM) y en la emisión.
 */
export const resolveOverlayDesignSize = (
  options: { designSize?: IDesignSize; designAspectRatio?: string },
  fallback: IDesignSize,
): IDesignSize => {
  if (options.designSize) return options.designSize;
  const ratio = options.designAspectRatio;
  if (ratio && ratio !== "free") return designSizeForRatio(ratio);
  return fallback;
};
